<template>
  <div class="view">
    <div class="view__block" ref="viewRef">
      <div class="view__top">
        <div @click="closeView()">
          <svg width="43" viewBox="0 0 43 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="21.5" cy="21.4998" r="21.5" fill="currentColor"/>
            <path d="M13.3084 20.1398L27.7084 13.8398V16.5038L16.0084 21.3998L27.7084 26.2958V28.9598L13.3084 22.6598V20.1398Z"
                  fill="white"/>
          </svg>
          <p>{{ t('buttons.backToMarket') }}</p>
        </div>
      </div>
      <div class="view__main">
        <img class="view__image" :src="product.images.edges[0].node.image" :alt="product.name" >
        <div class="view__main-info">
          <router-link
              :to="translations.i18nRoute({ name: 'product', params: { productSlug: product.slug } })"
              class="view__name"
              @click="closeView()"
          >
            {{ product.name }}
          </router-link>
          <div class="view__price">
            <span style="color: #3b3b3b">€</span>            <p>{{ product.price }}</p>
          </div>
          <p class="view__description">{{ product.description }}</p>
          <div class="view__buttons">
            <div @click="overwriteCart">
              <img src="@icons/cartIcon.svg" alt="cart">
              <p>{{ isProductInCart ? t('buttons.remove') : t('buttons.cart') }}</p>
            </div>
            <div @click="overwriteWishlist">
              <svg xmlns="http://www.w3.org/2000/svg" width="36" viewBox="0 0 18 17" fill="none">
                <path d="M9.40992 16.3264C13.7219 16.3264 17.2174 12.8309 17.2174 8.51892C17.2174 4.20696 13.7219 0.711426 9.40992 0.711426C5.09795 0.711426 1.60242 4.20696 1.60242 8.51892C1.60242 12.8309 5.09795 16.3264 9.40992 16.3264Z" stroke="#DD6196" stroke-width="1.30125" stroke-linecap="round" stroke-linejoin="round"/>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.40724 6.22969C8.62675 5.31723 7.32521 5.07177 6.34731 5.90732C5.3694 6.74287 5.23172 8.13986 5.99968 9.12807C6.49156 9.76102 7.75128 10.9348 8.59344 11.6969C8.8733 11.9501 9.01322 12.0767 9.18083 12.1275C9.32492 12.1712 9.48957 12.1712 9.63365 12.1275C9.80126 12.0767 9.94119 11.9501 10.2211 11.6969C11.0632 10.9348 12.3229 9.76102 12.8148 9.12807C13.5828 8.13986 13.4619 6.73408 12.4672 5.90732C11.4725 5.08056 10.1877 5.31723 9.40724 6.22969Z" stroke="#DD6196" :fill="isProductInWishlist ? 'red' : 'none'" stroke-width="1.30125" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <p>{{ t('buttons.wishlist') }}</p>
            </div>
          </div>
        </div>
      </div>
      <img class="view__block-bg" src="@images/viewBg.png" alt="bg">
    </div>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import {computed, ref} from "vue";
import {onClickOutside} from "@vueuse/core";
import {useAppStore} from "@/stores/app.js";
import {useCartStore} from "@/stores/cart.js";
import {useWishlistStore} from "@/stores/wishlist.js";
import {Orders} from "@/core/api/orders.js";
import {ElNotification} from "element-plus";
import {Wishlist} from "@/core/api/wishlist.js";
import {useAuthStore} from "@/stores/auth.js";
import {useRouter} from "vue-router";
import translations from "@/core/helpers/translations.js";

const props = defineProps({
  product: Object
})

const {t} = useI18n()
const router = useRouter()
const appStore = useAppStore()
const authStore = useAuthStore()
const cartStore = useCartStore()
const wishlistStore = useWishlistStore()

const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : ''
})
const orderUuid = computed(() => {
  return cartStore.currentOrder ? cartStore.currentOrder.uuid : null
})
const wishlistUuid = computed(() => {
  return wishlistStore.wishlist ? wishlistStore.wishlist.uuid : ''
})
const user = computed(() => {
  return authStore.user ? authStore.user : null
})

const closeView = () => appStore.setActiveViewProduct(null)

const viewRef = ref(null)
onClickOutside(viewRef, () => closeView())

const isProductInCart = computed(() => {
  return cartStore.currentOrder?.orderProducts?.edges.find((prod) => prod.node.product.uuid === props.product.uuid)
})
const isProductInWishlist = computed(() => {
  const el = wishlistStore.wishlist.products?.edges.find(
      (el) => el?.node?.uuid === props.product.uuid
  );

  return !!el;
});

const overwriteCart = async () => {
  if (user.value) {
    if (isProductInCart.value) {
      const res = await Orders.overwriteCart("removeOrderProduct", accessToken.value, orderUuid.value, props.product.uuid);

      cartStore.setCurrentOrders(res.data.removeOrderProduct.order)
    } else {
      const res = await Orders.overwriteCart("addOrderProduct", accessToken.value, orderUuid.value, props.product.uuid);
      cartStore.setCurrentOrders(res.data.addOrderProduct.order)

      if (res.errors) {
        ElNotification({
          message: res.errors[0].message,
          type: "error"
        });
      } else {
        ElNotification({
          message: t('popup.addToCart', { product: props.product.name }),
          type: "success"
        });
      }
    }
  } else {
    await router.push({
      name: 'login',
      params: {
        locale: localStorage.getItem('digitgiftcode-user-locale') || 'en-gb'
      }
    })
  }
}

const overwriteWishlist = async () => {
  if (user.value) {
    if (isProductInWishlist.value) {
      const res = await Wishlist.overwriteWishlist('removeWishlistProduct', accessToken.value, wishlistUuid.value, props.product.uuid)
      wishlistStore.wishlist = res.data.removeWishlistProduct.wishlist
    } else {
      const res = await Wishlist.overwriteWishlist('addWishlistProduct', accessToken.value, wishlistUuid.value, props.product.uuid)
      wishlistStore.wishlist = res.data.addWishlistProduct.wishlist

      if (res.data.addWishlistProduct) {
        ElNotification({
          message: t('popup.addToWishlist', { product: props.product.name }),
          type: "success"
        });
      } else {
        ElNotification({
          message: res.errors[0].message,
          type: "error"
        });
      }
    }
  } else {
    await router.push({
      name: 'auth-login',
      params: {
        locale: localStorage.getItem('digitgiftcode-user-locale') || 'en-gb'
      }
    })
  }
}
</script>

<style lang="scss" scoped>
.view {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(3, 38, 53, 0.18);
  backdrop-filter: blur(10px);

  &__block {
    width: 900px;
    margin: 0 auto;
    filter: drop-shadow(0 4px 21.4px rgba(0, 0, 0, 0.25));
    border-radius: 16px;
    border: 2px solid #3b3b3b;
    background-color: $white;
    padding: 40px 60px;
    position: relative;
    top: 200px;

    &-bg {
      position: absolute;
      left: 0;
      bottom: 0;
    }
  }

  &__top {
    padding-bottom: 10px;
    border-bottom: 1px solid #EDEDED;
    position: relative;
    z-index: 1;

    & div {
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 15px;
      color: #DD6196;

      @include hover {
        color: #e63d86;
      }

      & svg {
        transition: 0.2s;
      }

      & p {
        transition: 0.2s;
        font-size: 24px;
        font-weight: 700;
        line-height: 27px;
      }
    }
  }

  &__main {
    margin-top: 30px;
    display: flex;
    align-items: stretch;
    gap: 100px;
    position: relative;
    z-index: 1;

    &-info {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }
  }

  &__image {
    width: 215px;
    object-fit: cover;
    border-radius: 16px;
    box-shadow: -4px 6px 9.8px 0 rgba(0, 0, 0, 0.15);
  }

  &__name {
    transition: 0.2s;
    color: #222;
    font-size: 24px;
    font-weight: 700;
    line-height: 27px;

    @include hover {
      color: #DD6196;
    }
  }

  &__price {
    display: flex;
    align-items: center;
    gap: 5px;

    & p {
      color: #222;
      font-size: 24px;
      font-weight: 700;
      line-height: 27px;
    }
  }

  &__description {
    color: #666;
    font-family: "Hanken Grotesk", sans-serif;
    font-size: 16px;
    line-height: 22px;
  }

  &__buttons {
    margin-top: auto;
    display: flex;
    align-items: center;
    gap: 20px;

    & div {
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 10px;

      @include hover {
        & p {
          color: #DD6196;
        }
      }

      &:first-child {
        border-right: 1px solid #D9D9D9;
        padding-right: 20px;
      }

      & img {
        width: 36px;
      }

      & p {
        transition: 0.2s;
        color: #666;
        font-size: 24px;
        font-weight: 700;
        line-height: 27px;
      }
    }
  }
}

@media (max-width: 1300px) {
  .view {
    &__block {
      top: 125px;
    }

    &__top {
      & div {
        & svg {
          width: 30px;
        }

        & p {
          font-size: 20px;
        }
      }
    }

    &__name {
      font-size: 20px;
    }

    &__price {
      & p {
        font-size: 20px;
      }
    }

    &__buttons {
      & div {
        & p {
          font-size: 20px;
        }
      }
    }

    &__image {
      width: 300px;
      height: 225px;
    }
  }
}

@media (max-width: 950px) {
  .view {
    &__block {
      width: 85%;
      padding: 20px 30px;
    }

    &__main {
      justify-content: space-between;
      gap: 25px;
    }
  }
}

@media (max-width: 800px) {
  .view {
    &__main {
      margin-top: 20px;
      flex-direction: column;
      align-items: center;
    }

    &__image {
      width: 200px;
      height: 150px;
    }
  }
}
</style>